import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { authHeaders } from '../utils/api';
import {
  Fuel, Clock, ClipboardList, Wallet, Boxes, Users, Download, Loader2,
  ScanSearch, CalendarRange, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBRL = (v) => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => (v ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

function Stat({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3">
      <p className="text-gray-500 text-xs mb-1">{label}</p>
      <p className="text-white text-lg font-bold leading-tight">{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-0.5">{sub}</p>}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 rounded-lg bg-yellow-500/10 text-yellow-500"><Icon size={16} /></div>
        <h2 className="text-white font-semibold text-sm">{title}</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">{children}</div>
    </div>
  );
}

export default function MasterRaioX() {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [periodo, setPeriodo] = useState({ inicio: '', fim: '' });

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/tenants`, { headers: authHeaders() });
        if (r.ok) {
          const list = await r.json();
          setTenants(list);
          if (list.length) setTenantId(list[0].id);
        }
      } catch { toast.error('Erro ao carregar empresas'); }
    })();
  }, []);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    if (periodo.inicio) p.set('data_inicio', periodo.inicio);
    if (periodo.fim) p.set('data_fim', periodo.fim);
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [periodo]);

  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/api/master/raiox/${tenantId}${qs()}`, { headers: authHeaders() });
        if (r.ok) setData(await r.json());
        else toast.error('Erro ao carregar o Raio-X');
      } catch { toast.error('Erro de conexão'); }
      finally { setLoading(false); }
    })();
  }, [tenantId, qs]);

  const aplicarPeriodo = () => setPeriodo({ inicio, fim });
  const limparPeriodo = () => { setInicio(''); setFim(''); setPeriodo({ inicio: '', fim: '' }); };

  const exportarPDF = async () => {
    setExporting(true);
    try {
      const r = await fetch(`${API_URL}/api/master/raiox/${tenantId}/pdf${qs()}`, { headers: authHeaders() });
      if (!r.ok) { toast.error('Erro ao gerar PDF'); return; }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `raiox_${(data?.tenant?.name || 'empresa').replace(/ /g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Raio-X exportado em PDF');
    } catch { toast.error('Erro ao exportar PDF'); }
    finally { setExporting(false); }
  };

  const c = data?.combustivel, h = data?.horas, os = data?.ordens_servico;
  const cc = data?.conta_corrente, fin = data?.financeiro, cp = data?.contas_pagar;
  const alm = data?.almoxarifado, cad = data?.cadastros;

  return (
    <div className="space-y-4" data-testid="master-raiox-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ScanSearch className="text-yellow-500" size={26} /> Raio-X da Empresa
          </h1>
          <p className="text-gray-500 text-sm">Consolidado numérico de toda a movimentação do tenant</p>
        </div>
        <button onClick={exportarPDF} disabled={!data || exporting} data-testid="raiox-export-pdf-btn"
          className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50">
          {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          Exportar PDF
        </button>
      </div>

      {/* Abas por tenant */}
      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="raiox-tenant-tabs">
        {tenants.map(t => (
          <button key={t.id} onClick={() => setTenantId(t.id)} data-testid={`raiox-tab-${t.id}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              tenantId === t.id
                ? 'bg-yellow-500 text-black border-yellow-500'
                : 'bg-[#111] text-gray-400 border-gray-800 hover:border-yellow-500/40 hover:text-white'
            }`}>
            {t.name}
          </button>
        ))}
      </div>

      {/* Filtro de período */}
      <div className="flex flex-wrap items-end gap-3 bg-[#111] border border-gray-800 rounded-xl p-4">
        <div className="flex items-center gap-2 text-gray-400 text-sm"><CalendarRange size={16} /> Período:</div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Início</label>
          <input type="date" value={inicio} onChange={e => setInicio(e.target.value)} data-testid="raiox-data-inicio"
            className="bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Fim</label>
          <input type="date" value={fim} onChange={e => setFim(e.target.value)} data-testid="raiox-data-fim"
            className="bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm" />
        </div>
        <button onClick={aplicarPeriodo} data-testid="raiox-aplicar-periodo-btn"
          className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-700 transition-colors">Aplicar</button>
        {(periodo.inicio || periodo.fim) && (
          <button onClick={limparPeriodo} data-testid="raiox-limpar-periodo-btn"
            className="flex items-center gap-1 px-3 py-2 text-gray-400 hover:text-white text-sm transition-colors">
            <X size={14} /> Limpar
          </button>
        )}
        {(periodo.inicio || periodo.fim) && (
          <span className="text-xs text-yellow-500">Filtrado: {periodo.inicio || '...'} a {periodo.fim || '...'}</span>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && data && (
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Empresa: <span className="text-white font-medium">{data.tenant.name}</span> · Gerado em {data.gerado_em} · Cadastros ativos são snapshot atual (não afetados pelo período)
          </p>

          <Section icon={Fuel} title="Combustível">
            <Stat testid="raiox-compras-litros" label="Litros comprados" value={`${fmtNum(c.compras_litros)} L`} sub={`${c.compras_qtd} compras`} />
            <Stat testid="raiox-compras-valor" label="Valor das compras" value={fmtBRL(c.compras_valor)} />
            <Stat testid="raiox-abast-litros" label="Litros abastecidos" value={`${fmtNum(c.abastecimentos_litros)} L`} sub={`${c.abastecimentos_qtd} abastecimentos`} />
            <Stat testid="raiox-abast-custo" label="Custo dos abastecimentos" value={fmtBRL(c.abastecimentos_custo)} />
          </Section>

          <Section icon={Clock} title="Horas e Ordens de Serviço">
            <Stat testid="raiox-horas-trabalhadas" label="Horas trabalhadas (O.S. fechadas)" value={`${fmtNum(h.horas_trabalhadas)} h`} />
            <Stat testid="raiox-horas-nao-op" label="Horas não operacionais" value={`${fmtNum(h.horas_nao_operacionais)} h`} sub={`${h.hno_registros} registros`} />
            <Stat testid="raiox-os-abertas" label="O.S. abertas" value={os.abertas} />
            <Stat testid="raiox-os-fechadas" label="O.S. fechadas" value={os.fechadas} />
            <Stat testid="raiox-os-operacionais" label="O.S. operacionais" value={os.operacionais} />
            <Stat testid="raiox-os-manutencao" label="O.S. de manutenção" value={os.manutencao} />
            <Stat testid="raiox-os-canceladas" label="O.S. canceladas" value={os.canceladas} />
          </Section>

          <Section icon={Wallet} title="Movimentação Financeira">
            <Stat testid="raiox-cc-creditos" label="Conta Corrente — Créditos" value={fmtBRL(cc.creditos_valor)} sub={`${cc.creditos_qtd} lançamentos`} />
            <Stat testid="raiox-cc-debitos" label="Conta Corrente — Débitos" value={fmtBRL(cc.debitos_valor)} sub={`${cc.debitos_qtd} lançamentos`} />
            <Stat testid="raiox-cc-saldo" label="CC — Saldo movimentado" value={fmtBRL(cc.saldo_movimentado)} />
            <Stat testid="raiox-fin-entradas" label="Financeiro — Entradas" value={fmtBRL(fin.entradas_valor)} sub={`${fin.entradas_qtd} lançamentos`} />
            <Stat testid="raiox-fin-saidas" label="Financeiro — Saídas" value={fmtBRL(fin.saidas_valor)} sub={`${fin.saidas_qtd} lançamentos`} />
            <Stat testid="raiox-cp-aberto" label="Contas a Pagar — Em aberto" value={fmtBRL(cp.aberto_valor)} sub={`${cp.aberto_qtd} títulos`} />
            <Stat testid="raiox-cp-pago" label="Contas a Pagar — Pagas" value={fmtBRL(cp.pago_valor)} sub={`${cp.pago_qtd} títulos`} />
          </Section>

          <Section icon={Boxes} title="Almoxarifado">
            <Stat testid="raiox-alm-itens" label="Itens cadastrados" value={alm.itens} />
            <Stat testid="raiox-alm-entradas" label="Entradas (movimentos)" value={alm.entradas} />
            <Stat testid="raiox-alm-saidas" label="Saídas (movimentos)" value={alm.saidas} />
          </Section>

          <Section icon={Users} title="Cadastros Ativos (snapshot)">
            <Stat testid="raiox-cad-maquinas" label="Máquinas e veículos" value={cad.maquinas} />
            <Stat testid="raiox-cad-operadores" label="Operadores" value={cad.operadores} />
            <Stat testid="raiox-cad-colaboradores" label="Colaboradores" value={cad.colaboradores} />
            <Stat testid="raiox-cad-locais" label="Locais de operação" value={cad.locais} />
            <Stat testid="raiox-cad-setores" label="Setores de operação" value={cad.setores_operacao} />
            <Stat testid="raiox-cad-usuarios" label="Usuários" value={cad.usuarios} />
            <Stat testid="raiox-cad-fornecedores" label="Fornecedores" value={cad.fornecedores} />
            <Stat testid="raiox-cad-inventario" label="Itens de inventário" value={cad.inventario_ativos} />
          </Section>
        </div>
      )}
    </div>
  );
}
